import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { ErrorStateMatcher, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { CommonModule, NgIf } from '@angular/common';
import { UsersService } from '../users-service/users.service';
import { GrpcStatusEvent } from '@ngx-grpc/common';
import {
    MyErrorStateMatcher,
    passwordMatchValidator,
    strongPasswordValidator
} from './form-validators';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import {
    RegisterRequest,
    UserGroupDto
} from '../../../protos/generated/admin.pb';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-add-user',
    imports: [
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        ReactiveFormsModule,
        MatCheckboxModule,
        CommonModule,
        NgIf,
        RouterLink,
        MatProgressSpinnerModule,
        TranslatePipe
    ],
    templateUrl: './add-user.component.html',
    styleUrl: './add-user.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [{ provide: ErrorStateMatcher, useClass: MyErrorStateMatcher }]
})
export class AddUserComponent implements OnInit, OnDestroy {
    addUserForm!: FormGroup;
    matcher = new MyErrorStateMatcher();
    userGroups: UserGroupDto[] = [];
    registerRequest: RegisterRequest = new RegisterRequest();
    addUserSuccess: boolean = false;
    addUserError: boolean = false;
    addUserErrorMessage: string = '';
    hidePassword: boolean = true;
    hidePasswordConfirm: boolean = true;
    isLoading: boolean = false;
    private subscription = new Subscription();

    ngOnInit(): void {
        this.addUserForm = this.fb.group(
            {
                firstName: ['', Validators.required],
                lastName: ['', Validators.required],
                email: ['', [Validators.required, Validators.email]],
                phone: ['', Validators.required],
                username: [
                    '',
                    [Validators.required, Validators.pattern(/^\S*$/)]
                ],
                userGroupId: ['', Validators.required],
                password: ['', [Validators.required, strongPasswordValidator]],
                confirmPassword: ['', Validators.required]
            },
            { validator: passwordMatchValidator }
        );
        this.userService.getAllUserGroups().subscribe(groups => {
            this.userGroups = groups.userGroups;
        });
    }

    // isToggled
    isToggled = false;

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private userService: UsersService,
        private route: Router
    ) {
        this.themeService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });
    }

    addUser(): void {
        this.isLoading = true;
        const request = new RegisterRequest({
            username: this.addUserForm.get('username')?.value,
            email: this.addUserForm.get('email')?.value,
            password: this.addUserForm.get('password')?.value,
            confirmPassword: this.addUserForm.get('confirmPassword')?.value,
            firstName: this.addUserForm.get('firstName')?.value,
            lastName: this.addUserForm.get('lastName')?.value,
            phoneNumber: this.addUserForm.get('phone')?.value,
            userGroupId: this.addUserForm.get('userGroupId')?.value
        });

        this.userService.registerUser(request).subscribe({
            next: () => {
                this.addUserSuccess = true;
                this.closeAlert();
                setTimeout(() => {
                    this.route.navigate(['users/users-list']);
                }, 3000);
            },
            error: (error: GrpcStatusEvent) => {
                this.addUserErrorMessage = error.statusMessage;
                this.closeAlert();
                this.isLoading = false;
            },
            complete: () => {
                this.addUserForm.reset({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    username: '',
                    userGroupId: '',
                    password: '',
                    confirmPassword: ''
                });

                // Ensure confirmPassword field errors are cleared
                this.addUserForm.get('confirmPassword')?.setErrors(null);
                this.addUserForm.updateValueAndValidity(); // Force re-evaluation of validation

                // Reset form state
                this.addUserForm.markAsPristine();
                this.addUserForm.markAsUntouched();
                this.isLoading = false;
            }
        });
    }

    closeAlert() {
        setTimeout(() => {
            this.addUserErrorMessage = '';
            this.addUserSuccess = false;
        }, 3000);
    }

    closeAlertManully() {
        this.addUserErrorMessage = '';
        this.addUserSuccess = false;
    }

    //on destroy
    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
